import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/app/ui-bits";
import { toast } from "sonner";
import { CalendarCheck, Funnel, Phone, Envelope, User } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import ExportMenu from "@/components/app/ExportMenu";

const STATUS_OPTIONS = [
  { v: "new", l: "New" },
  { v: "confirmed", l: "Confirmed" },
  { v: "rescheduled", l: "Rescheduled" },
  { v: "completed", l: "Completed" },
  { v: "cancelled", l: "Cancelled" },
];

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const { data } = await api.get(`/appointments${q}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateField = async (field, value) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/appointments/${selected.id}`, { [field]: value });
      setSelected(data);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="appointments-page">
      <PageHeader
        title="Appointments"
        description="Client & visitor bookings received from the public landing page."
        actions={<ExportMenu module="appointments" filters={{ status: statusFilter !== "all" ? statusFilter : undefined }} />}
      />

      <div className="space-y-4 p-5 md:p-8">
        <div className="dc-card p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Funnel size={16} /> <span className="font-semibold">Filter by status</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {[{ v: "all", l: "All" }, ...STATUS_OPTIONS].map((s) => (
              <button
                key={s.v}
                onClick={() => setStatusFilter(s.v)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono-alt uppercase tracking-wider border transition ${
                  statusFilter === s.v
                    ? "bg-[#1E5CBA] border-[#1E5CBA] text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
                data-testid={`filter-${s.v}`}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>

        <div className="dc-card overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarCheck size={40} className="mx-auto text-slate-300" />
              <div className="mt-3 text-sm font-semibold text-slate-700">No appointments yet</div>
              <p className="mt-1 text-xs text-slate-500">Requests submitted from the public site will appear here.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <table className="hidden w-full md:table">
                <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">Purpose</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Contact</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((a) => (
                    <tr
                      key={a.id}
                      className="cursor-pointer text-sm hover:bg-slate-50"
                      onClick={() => setSelected(a)}
                      data-testid={`appt-row-${a.id}`}
                    >
                      <td className="px-5 py-3">
                        <div className="font-semibold text-slate-900">{a.full_name}</div>
                        {a.company && <div className="text-xs text-slate-500">{a.company}</div>}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{a.purpose}</td>
                      <td className="px-5 py-3 text-slate-700">
                        {formatDate(a.preferred_date)}
                        {a.preferred_time && <span className="ml-1 text-xs text-slate-500">{a.preferred_time}</span>}
                      </td>
                      <td className="px-5 py-3 text-xs">
                        <div className="truncate text-slate-700">{a.email}</div>
                        {a.phone && <div className="truncate text-slate-500">{a.phone}</div>}
                      </td>
                      <td className="px-5 py-3"><StatusBadge status={a.status === "new" ? "pending" : a.status === "confirmed" ? "active" : a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile cards */}
              <ul className="divide-y divide-slate-100 md:hidden">
                {items.map((a) => (
                  <li key={a.id} className="p-4 cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelected(a)} data-testid={`appt-card-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-900">{a.full_name}</div>
                        {a.company && <div className="truncate text-xs text-slate-500">{a.company}</div>}
                      </div>
                      <StatusBadge status={a.status === "new" ? "pending" : a.status === "confirmed" ? "active" : a.status} />
                    </div>
                    <div className="mt-2 text-sm text-slate-700">{a.purpose}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {formatDate(a.preferred_date)}{a.preferred_time ? ` · ${a.preferred_time}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="appt-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selected.full_name}</DialogTitle>
                <DialogDescription>
                  {selected.company || "No company"} · reference {selected.id.slice(0, 8).toUpperCase()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                <InfoRow icon={<Envelope size={14} />} label="Email" value={selected.email} />
                <InfoRow icon={<Phone size={14} />} label="Phone" value={selected.phone || "—"} />
                <InfoRow icon={<CalendarCheck size={14} />} label="Preferred"
                         value={`${formatDate(selected.preferred_date)}${selected.preferred_time ? " · " + selected.preferred_time : ""}`} />
                <div>
                  <div className="text-xs font-mono-alt uppercase tracking-widest text-slate-500">Purpose</div>
                  <div className="mt-1 font-medium text-slate-800">{selected.purpose}</div>
                </div>
                {selected.message && (
                  <div>
                    <div className="text-xs font-mono-alt uppercase tracking-widest text-slate-500">Message</div>
                    <p className="mt-1 rounded-md bg-slate-50 p-3 text-sm text-slate-700">{selected.message}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 pt-2 md:grid-cols-2">
                  <div>
                    <Label className="overline">Status</Label>
                    <Select value={selected.status} onValueChange={(v) => updateField("status", v)} disabled={busy}>
                      <SelectTrigger className="mt-1" data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline">Assigned to</Label>
                    <Input
                      className="mt-1"
                      placeholder="name@descon.group"
                      defaultValue={selected.assigned_to || ""}
                      onBlur={(e) => e.target.value !== selected.assigned_to && updateField("assigned_to", e.target.value)}
                      data-testid="appt-assigned-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="overline">Internal notes</Label>
                  <textarea
                    className="mt-1 w-full rounded-md border border-slate-200 p-3 text-sm"
                    rows={3}
                    defaultValue={selected.internal_notes || ""}
                    onBlur={(e) => e.target.value !== selected.internal_notes && updateField("internal_notes", e.target.value)}
                    data-testid="appt-notes-input"
                  />
                </div>
                <div className="border-t border-slate-100 pt-2 text-[11px] font-mono-alt uppercase tracking-widest text-slate-400">
                  Received {formatDateTime(selected.created_at)}
                </div>
              </div>
              <DialogFooter>
                <AuditTrailDrawer entity="appointment" entityId={selected.id} label={selected.contact_name || selected.subject || "Appointment"} />
                <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="mt-0.5 text-slate-400">{icon}</span>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className="truncate font-medium text-slate-800">{value}</div>
    </div>
  </div>
);
